import ChartLoading from '@/components/common/charts/ChartLoading';
import { FlexRow } from '@/components/common/Flex';
import { LinkBroken01 } from '@/components/common/icons';
import { useQuery } from '@apollo/client';
import { Box, FlexProps } from '@chakra-ui/react';
import { ExternalFinancialEntity, isDebt } from '@common/financialEntities';
import { FieldConfig } from '@common/financialEntities/config';
import { FinancialEntityValueSeriesDocument } from '@common/graphql/generated';
import { isDisconnectedOrError } from '@common/react/financialEntities';
import { UserInfo } from '@common/react/user';
import { dt } from '@common/util/dateTime';
import { FEValueNode, ValueSeries } from '@common/valueSeries';
import { ParentSize } from '@visx/responsive';
import { FC, useRef, useState } from 'react';
import { useBalanceChangeDateRangeState } from '../common/BalanceChange';
import {
    ChartRangeSelector,
    ChartRangeStepperButton,
    useChartRangeControls,
} from '../common/charts/ChartRangeControls';
import ErrorBoundary from '../common/ErrorBoundary';
import { FlexCol } from '../common/Flex';
import HistoricalBalanceChange from '../common/HistoricalBalanceChange';
import InputField from '../common/InputField';
import SquareIconButton from '../common/SquareIconButton';
import { disabledStyleProps } from '../common/styleHelpers';
import { Title } from '../common/Typography';
import FinancialEntityChart from './FinancialEntityChart';
import { formatValue } from './helpers';

export interface FinancialEntityDetailChartViewProps extends FlexProps {
    fe: ExternalFinancialEntity;
    form: Record<string, any>;
    field: FieldConfig;
    linkedFields: string[];
    onFieldChange: (name: string, value: any) => void;
    userInfo: UserInfo;
}

const FinancialEntityDetailChartView: FC<
    FinancialEntityDetailChartViewProps
> = ({ fe, form, field, linkedFields, onFieldChange, userInfo, ...props }) => {
    const isMyFE = fe.isMine;
    const [balanceChangeDateRange, setBalanceChangeDateRange] =
        useBalanceChangeDateRangeState();

    const balanceIsEditable = isMyFE && !linkedFields.includes(field.name);
    const [isEditing, setIsEditing] = useState(false);
    const ref = useRef<HTMLInputElement>(null);
    const handleClickEditing = () => {
        const newIsEditing = !isEditing;
        setIsEditing(newIsEditing);
        if (newIsEditing) {
            ref.current?.focus();
        }
    };

    const isDisconnected = isDisconnectedOrError(fe);

    const minDate = dt(fe.earliestSnapshot?.dateTime).toDateStr();
    const maxDate = userInfo.finalDate;

    const initialType = fe.linkStatus === 'MANUAL' ? '1Y' : '1M';

    const { chartRangeType, range, onIncrement, onDecrement, onChangeType } =
        useChartRangeControls({
            initialType,
            minDate,
            maxDate,
            key: 'financial-entity-detail',
        });

    const { data: valueSeriesData, loading: valueSeriesLoading } = useQuery(
        FinancialEntityValueSeriesDocument,
        {
            variables: {
                financialEntityId: fe.id,
                range,
                params: form,
                currentDate: dt().toDateStr(),
            },
        }
    );

    if (!userInfo) return null;

    return (
        <FlexCol shrink={0} grow={0} {...props}>
            <FlexRow
                shrink={0}
                grow={0}
                justify="space-between"
                align="flex-end"
                mb={10}
            >
                <FlexRow wrap="wrap" alignItems="flex-end">
                    {balanceIsEditable ? (
                        <FlexRow top={1} mr={1} align="flex-end">
                            <InputField
                                ref={ref}
                                tabIndex={1}
                                {...field}
                                value={form[field.name]}
                                onChange={(value) =>
                                    onFieldChange(field.name, value)
                                }
                                labelProps={{ whiteSpace: 'nowrap' }}
                                inputProps={{
                                    fontSize: '2xl',
                                    fontWeight: 500,
                                    variant: 'flushed',
                                    w: 32,
                                }}
                                isReadOnly={!isEditing}
                            />
                            <SquareIconButton
                                iconName="Pencil01"
                                variant="ghost"
                                colorScheme="gray"
                                aria-label="edit"
                                onClick={handleClickEditing}
                                onClickEvent="Edit Financial Entity Balance Button Clicked"
                                mb={2}
                            />
                        </FlexRow>
                    ) : (
                        <Title
                            lineHeight={1}
                            {...disabledStyleProps(isDisconnected)}
                        >
                            {formatValue(form[field.name], field)}
                            {isDisconnected && (
                                <LinkBroken01 ml={2} size="md" color="black" />
                            )}
                        </Title>
                    )}
                    <HistoricalBalanceChange
                        dateRange={balanceChangeDateRange}
                        onChangeDateRange={setBalanceChangeDateRange}
                        positiveAccounts={[fe]}
                        swapColors={isDebt(fe)}
                        ml={3}
                        mb={balanceIsEditable ? 3 : 0}
                    />
                </FlexRow>
            </FlexRow>
            <FlexRow shrink={0} grow={0}>
                <Box h={280} w="full" position="relative">
                    <ChartRangeSelector
                        type={chartRangeType}
                        onChange={onChangeType}
                    />
                    <ChartRangeStepperButton
                        type="decrement"
                        onClick={onDecrement}
                    />
                    <ChartRangeStepperButton
                        type="increment"
                        onClick={onIncrement}
                    />
                    <ParentSize>
                        {(parent) => (
                            <ErrorBoundary>
                                {valueSeriesLoading || !valueSeriesData ? (
                                    <FlexRow
                                        width={parent.width}
                                        height={parent.height}
                                    >
                                        <ChartLoading />
                                    </FlexRow>
                                ) : (
                                    <FinancialEntityChart
                                        fe={fe}
                                        width={parent.width}
                                        height={parent.height}
                                        range={range}
                                        valueSeries={
                                            valueSeriesData.financialEntityValueSeries as ValueSeries<FEValueNode>
                                        }
                                    />
                                )}
                            </ErrorBoundary>
                        )}
                    </ParentSize>
                </Box>
            </FlexRow>
        </FlexCol>
    );
};

export default FinancialEntityDetailChartView;
